import { ReactNode } from "react";
import { Box, Stack } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import BrandLogo from "./BrandLogo";
import LanguageSwitcher from "./language";
import { PAGE_PX } from "../utils/layout";
import { useCustomColors } from "../hooks/useCustomColors";

interface AppHeaderProps {
  /** Right-hand slot. Signed-in shells pass the user chip and actions;
   *  the signed-out auth pages pass nothing, matching the shell's own
   *  logged-out bar. */
  children?: ReactNode;
}

const AppHeader = ({ children }: AppHeaderProps) => {
  const navigate = useNavigate();
  const { palette } = useTheme();
  const colors = useCustomColors();
  const isDark = palette.mode === "dark";

  return (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 1100,
        height: { xs: "64px", md: "76px" },
        px: PAGE_PX,
        display: "flex",
        alignItems: "center",
        gap: "16px",
        isolation: "isolate",
        background: isDark
          ? "radial-gradient(ellipse at 18% 50%, rgba(63, 10, 8, 0.18) 0%, transparent 34%), radial-gradient(ellipse at 82% 100%, rgba(63, 10, 8, 0.30) 0%, transparent 48%), linear-gradient(180deg, #060505 0%, #160708 44%, #3F0A08 78%, #260807 100%)"
          : colors.gradient.header,
        borderBottom: 0,
        boxShadow: isDark
          ? "inset 0 1px 0 rgba(255, 255, 255, 0.035), inset 0 -1px 0 rgba(0, 0, 0, 0.52), 0 10px 30px rgba(0, 0, 0, 0.35), 0 4px 18px rgba(99, 25, 26, 0.10)"
          : "0 4px 18px rgba(45, 33, 26, 0.10)",
        "&::after": {
          content: '""',
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          height: "1px",
          background: isDark ? "linear-gradient(90deg, transparent 0%, rgba(99, 25, 26, 0.65) 18%, rgba(212, 175, 55, 0.70) 50%, rgba(99, 25, 26, 0.65) 82%, transparent 100%)" : colors.gradient.divider,
          boxShadow: isDark ? "0 0 10px rgba(212, 175, 55, 0.10)" : `0 0 10px ${colors.tint.gold}`,
        },
      }}
    >
      <BrandLogo height={{ xs: 30, md: 40 }} onClick={() => navigate("/")} />

      <Stack direction="row" alignItems="center" sx={{ ml: "auto", gap: "10px" }}>
        {/* Drawer-only on xs, where the bar is already full. */}
        <Box sx={{ width: 132, flexShrink: 0, display: { xs: "none", sm: "block" } }}>
          <LanguageSwitcher />
        </Box>
        {children}
      </Stack>
    </Box>
  );
};

export default AppHeader;
